/**
 * Start a new audit run: project, tracks, ACTIVE packs, quick scan.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, KBPackListItem, ProjectListItem } from '../lib/api'
import { Modal } from './design-system/Modal'
import { Button } from './design-system/Button'
import { loadReviewerPreferences } from '../lib/preferences'
import { useLocale } from '../contexts/LocaleContext'
import { trackLabel } from '../lib/i18n'

const TRACK_IDS = ['code_quality', 'security', 'api', 'infrastructure', 'business_logic'] as const

interface StartAuditDialogProps {
  isOpen: boolean
  onClose: () => void
  initialProjectId?: string
}

export function StartAuditDialog({ isOpen, onClose, initialProjectId }: StartAuditDialogProps) {
  const { t, locale } = useLocale()
  const navigate = useNavigate()
  const prefs = loadReviewerPreferences()
  const [projects, setProjects] = useState<ProjectListItem[]>([])
  const [packs, setPacks] = useState<KBPackListItem[]>([])
  const [projectId, setProjectId] = useState(initialProjectId || '')
  const [tracks, setTracks] = useState<string[]>(['code_quality', 'security'])
  const [selectedPackIds, setSelectedPackIds] = useState<string[]>([])
  const [quickScan, setQuickScan] = useState(prefs.defaultQuickScan)
  const [includeAi, setIncludeAi] = useState(prefs.defaultIncludeAi)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setError(null)
    setProjectId(initialProjectId || '')
    Promise.all([
      api.listProjects(),
      api.listKBPacks({ page: 1, page_size: 100 }),
    ])
      .then(([projectData, packData]) => {
        setProjects(projectData.projects)
        setPacks(packData.items)
        if (!initialProjectId && projectData.projects.length === 1) {
          setProjectId(projectData.projects[0].id)
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : t('dialog.failedLoad'))
      })
  }, [isOpen, initialProjectId])

  function toggleTrack(id: string) {
    setTracks((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    )
  }

  function togglePack(packId: string) {
    setSelectedPackIds((current) =>
      current.includes(packId) ? current.filter((item) => item !== packId) : [...current, packId]
    )
  }

  async function handleStart() {
    if (!projectId) {
      setError(t('dialog.selectProjectFirst'))
      return
    }
    if (tracks.length === 0) {
      setError(t('dialog.selectTrack'))
      return
    }
    try {
      setLoading(true)
      setError(null)
      const selectedPacks = packs
        .filter((pack) => selectedPackIds.includes(pack.id) || selectedPackIds.includes(pack.pack_id))
        .map((pack) => ({ pack_id: pack.pack_id, version: pack.version }))
      const result = await api.startAuditRun({
        project_id: projectId,
        requested_tracks: tracks,
        quick_scan: quickScan,
        include_ai_analysis: includeAi,
        selected_packs: selectedPacks.length ? selectedPacks : undefined,
      })
      onClose()
      navigate(`/audit/${result.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dialog.failedStart'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('dialog.startTitleUi')}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {t('dialog.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleStart}
            disabled={loading || projects.length === 0}
            data-testid="start-audit-submit"
          >
            {loading ? t('dialog.starting') : t('dialog.startTitleUi')}
          </Button>
        </>
      }
    >
      <div data-testid="start-audit-dialog" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {error && (
          <p className="text-sm" style={{ color: 'var(--reviewer-danger)', margin: 0 }}>
            {error}
          </p>
        )}
        {projects.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--reviewer-text-secondary)' }}>
            {t('dialog.noProjects')}
          </p>
        ) : (
          <label className="text-sm" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {t('dialog.project')}
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              data-testid="start-audit-project"
              style={{
                padding: '8px 12px',
                fontFamily: 'var(--reviewer-font-mono)',
                backgroundColor: 'var(--reviewer-bg)',
                border: '1px solid var(--reviewer-border)',
                color: 'var(--reviewer-text)',
              }}
            >
              <option value="">{t('dialog.selectProject')}</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.display_name}
                </option>
              ))}
            </select>
          </label>
        )}

        <fieldset style={{ border: '1px solid var(--reviewer-border)', padding: '12px', margin: 0 }}>
          <legend className="text-sm">{t('dialog.tracksLegend')}</legend>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {TRACK_IDS.map((id) => (
              <label key={id} className="text-sm" style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={tracks.includes(id)}
                  onChange={() => toggleTrack(id)}
                />
                {trackLabel(locale, id)}
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset style={{ border: '1px solid var(--reviewer-border)', padding: '12px', margin: 0 }}>
          <legend className="text-sm">{t('dialog.packsLegend')}</legend>
          {packs.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--reviewer-text-secondary)', margin: 0 }}>
              {t('dialog.noActivePacks')}
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {packs.map((pack) => (
                <label key={pack.id} className="text-sm" style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={selectedPackIds.includes(pack.pack_id) || selectedPackIds.includes(pack.id)}
                    onChange={() => togglePack(pack.pack_id)}
                  />
                  {pack.name} ({pack.pack_id}@{pack.version})
                </label>
              ))}
              <p className="text-xs" style={{ color: 'var(--reviewer-text-tertiary)', margin: 0 }}>
                {t('dialog.leavePacks')}
              </p>
            </div>
          )}
        </fieldset>

        <label className="text-sm" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input type="checkbox" checked={quickScan} onChange={(e) => setQuickScan(e.target.checked)} />
          {t('dialog.quickScan')}
        </label>
        <label className="text-sm" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input type="checkbox" checked={includeAi} onChange={(e) => setIncludeAi(e.target.checked)} />
          {t('dialog.includeAi')}
        </label>
      </div>
    </Modal>
  )
}
